import { clearJWTs } from '@sleepstation/auth-jwt'
import { SVG } from '@sleepstation/components'
import { LinkBase } from '@sleepstation/components-base'
import { HLayout } from '@sleepstation/components-layouts'
import { LAYOUT_ALIGN, LAYOUT_SPACING } from '@sleepstation/const'
import {
  backgroundColor,
  backgroundImageLinearGradient,
  mq,
  offset,
  paddingV,
  stylesIfElseProp,
  stylesIfPropTrue,
} from '@sleepstation/style-api-helpers'
import { constrainWide } from '@sleepstation/style-mixins'
import { SleepstationMarkSvg } from '@sleepstation/svgs-sleepstation-branding'
import { redirect, rem } from '@sleepstation/utils'
import PropTypes from 'prop-types'
import { lensPath, map, view } from 'ramda'
import React, { useContext } from 'react'
import styled, { css } from 'styled-components'
import ContentContext from '../../../../context/ContentContext'
import LocalStorageContext from '../../../../context/LocalStorageContext'
import headerBackgrounds from '../../../../svg/starscapes/headerBackgrounds'
import AccountNav from './AccountNav'
import RoutingContext from '../../../../context/RoutingContext'

// -----------------------------------------------------------------------------
// Const
// -----------------------------------------------------------------------------

const LOGO_WIDTHS = map(rem, [113, 185])

// -----------------------------------------------------------------------------
// Utils
// -----------------------------------------------------------------------------

const headerPath = lensPath([`app`, `header`])

const headerView = view(headerPath)

const toBackgroundImage = (size) => (props) => {
  const image = props.images[size]
  return css`
    background-image: url(${image.url});
    background-position: ${image.position};
  `
}

const handleLogout = (localStorageApi, logoutPath) => () => {
  clearJWTs(localStorageApi)
  redirect(logoutPath)
}

// -----------------------------------------------------------------------------
// Sub-components
// -----------------------------------------------------------------------------

const Layout = styled.div`
  position: relative;
  ${stylesIfElseProp(
    `isFixed`,
    css`
      position: absolute;
      top: 0;
      right: 0;
      left: 0;
      z-index: 2;
    `,
    css`
      ${backgroundColor(`backgroundStart`)}
      ${backgroundImageLinearGradient()}
    `
  )}

  ${backgroundImageLinearGradient()}
  ${stylesIfPropTrue(
    `hideBackground`,
    css`
      background-image: none;
    `
  )}
`

const Inner = styled(HLayout).attrs({
  align: LAYOUT_ALIGN.CENTER,
  spacing: LAYOUT_SPACING.BETWEEN,
})`
  ${constrainWide()}
  ${paddingV([0.5, 0.75])}
`

const LogoWrapper = styled.div`
  svg path {
    fill: white;
  }
`

const LogoLink = styled(LinkBase)``

const Image = styled.div`
  position: absolute;
  ${offset(0)}
  background-repeat: no-repeat;

  ${mq(`xSmallOnly`)} {
    ${toBackgroundImage(`xSmall`)}
  }

  ${mq(`smallOnly`)} {
    ${toBackgroundImage(`small`)}
  }

  ${mq(`mediumUp`)} {
    ${toBackgroundImage(`medium`)}
  }
`

// -----------------------------------------------------------------------------
// Component
// -----------------------------------------------------------------------------

const AppHeader = ({
  isFixed,
  hideBackground,
  accoundNavIsHidden,
  logoutPath,
}) => {
  const content = useContext(ContentContext)
  const localStorageApi = useContext(LocalStorageContext)
  const {
    appRouting: { homePath },
  } = useContext(RoutingContext)
  const {
    accountNav: { logout },
  } = headerView(content)

  return (
    <Layout isFixed={isFixed} hideBackground={hideBackground}>
      <Inner>
        {!hideBackground && <Image images={headerBackgrounds} />}
        <LogoLink href={homePath} forceOpenInSameWindow>
          <LogoWrapper>
            <SVG svg={SleepstationMarkSvg} widths={LOGO_WIDTHS} />
          </LogoWrapper>
        </LogoLink>
        {!accoundNavIsHidden && (
          <AccountNav
            onLogout={handleLogout(localStorageApi, logoutPath)}
            logoutLabel={logout.label}
          />
        )}
      </Inner>
    </Layout>
  )
}

AppHeader.propTypes = {
  isFixed: PropTypes.bool,
  hideBackground: PropTypes.bool,
  accoundNavIsHidden: PropTypes.bool,
  logoutPath: PropTypes.string.isRequired,
}

AppHeader.defaultProps = {
  isFixed: false,
  hideBackground: false,
  accoundNavIsHidden: false,
}

export default AppHeader
